import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';

// Thresholds ported from the prototype's own scroll handler: the bar is pinned
// while near the top, hides on a downward scroll past HIDE_AFTER, returns on any
// upward scroll, and the title fades in past TITLE_AFTER (QA 3.9-3.12). The 4px
// deadband stops trackpad jitter from flapping it.
const HIDE_AFTER = 130;
const TITLE_AFTER = 220;
const DEADBAND = 4;

export interface LessonActionBarProps {
  title: string;
  bookmarked: boolean;
  onBack: () => void;
  onToggleBookmark: () => void;
}

const CIRCLE = {
  flex: 'none',
  width: 40,
  height: 40,
  borderRadius: '9999px',
};

export default function LessonActionBar({
  title, bookmarked, onBack, onToggleBookmark,
}: LessonActionBarProps) {
  const [visible, setVisible] = React.useState(true);
  const [showTitle, setShowTitle] = React.useState(false);
  const lastY = React.useRef(0);

  React.useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const last = lastY.current;
      setShowTitle(y > TITLE_AFTER);
      if (y <= HIDE_AFTER) setVisible(true);
      else if (y > last + DEADBAND) setVisible(false);
      else if (y < last - DEADBAND) setVisible(true);
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    // Sticky rather than fixed, so it sits in flow directly below the page header
    // and only pins once that header has scrolled past.
    <Box sx={{
      position: 'sticky',
      top: 0,
      zIndex: 15,
      bgcolor: '#fff',
      borderBottom: 1,
      borderColor: 'divider',
      transform: visible ? 'translateY(0)' : 'translateY(-135%)',
      transition: 'transform 260ms ease',
    }}
    >
      <Box sx={{
        maxWidth: 1000,
        mx: 'auto',
        px: '20px',
        py: '9px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}
      >
        <IconButton
          onClick={onBack}
          aria-label="Back to Lessons & Guides"
          sx={{
            ...CIRCLE,
            bgcolor: '#F2E7FE',
            color: '#3700B3',
            '&:hover': { bgcolor: '#E6D8FB' },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 22 }} />
        </IconButton>

        <Typography sx={{
          flex: 1,
          minWidth: 0,
          textAlign: 'center',
          fontWeight: 600,
          fontSize: '15px',
          color: '#23036A',
          // Only appears once the hero has scrolled away.
          opacity: showTitle ? 1 : 0,
          transition: 'opacity 220ms ease',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        >
          {title}
        </Typography>

        <IconButton
          onClick={onToggleBookmark}
          aria-label={bookmarked ? 'Remove from bookmarks' : 'Add to bookmarks'}
          sx={{
            ...CIRCLE,
            color: bookmarked ? '#3700B3' : 'rgba(0,0,0,0.5)',
            '&:hover': { bgcolor: '#F2E7FE' },
          }}
        >
          {bookmarked ? <BookmarkIcon sx={{ fontSize: 24 }} /> : <BookmarkBorderIcon sx={{ fontSize: 24 }} />}
        </IconButton>
      </Box>
    </Box>
  );
}
